import * as angular from 'angular';
import { gpHome } from '../app';
import {
    ISubscriptionManagementApi,
    LicenseAssignmentRequest,
    LicenseSummary,
    LicenseTypeDefinition,
    PersonalSubscriptionUpsertRequest,
    SeatUpdateRequest,
    SubscriptionLinkRequest,
    SubscriptionManagementServiceName,
    SubscriptionSummary,
    TeamMemberInviteRequest,
    TeamMemberRoleUpdateRequest,
    TeamSummary,
    TeamUpsertRequest,
    TeamRoleDefinition,
    TeamInviteSummary,
} from './subscription-management-contract';

type Nullable<T> = T | null | undefined;

class SubscriptionManagementMockService implements ISubscriptionManagementApi {

    static $inject = ['$q', '$timeout'];
    static serviceName = SubscriptionManagementServiceName;

    private readonly currentUserId = 'user-001';

    private licenseTypes: LicenseTypeDefinition[] = [
        { key: 'viewer', name: 'Viewer', description: 'Read-only access to shared plans.', defaultSeats: 1 },
        { key: 'planner', name: 'Planner', description: 'Full planning capabilities with collaboration.', defaultSeats: 1 },
        { key: 'pro', name: 'Pro', description: 'Planner plus enterprise integrations and automation.', defaultSeats: 1 },
    ];

    private subscriptions: SubscriptionSummary[] = [
        {
            id: 'sub-personal-001',
            planCode: 'planner-monthly',
            planName: 'Planner Monthly',
            description: 'Personal planner subscription billed monthly.',
            provider: 'stripe',
            status: 'active',
            seatsPurchased: 1,
            seatsInUse: 1,
            autoRenews: true,
            renewsUtc: '2024-10-01T00:00:00Z',
            licenseTypeKey: 'planner',
            billingEmail: 'you@example.com',
        },
        {
            id: 'sub-personal-002',
            planCode: 'viewer-free',
            planName: 'Viewer (Free)',
            provider: 'license',
            status: 'active',
            seatsPurchased: 1,
            seatsInUse: 0,
            autoRenews: false,
            licenseTypeKey: 'viewer',
        },
    ];

    private roleOptions: TeamRoleDefinition[] = [
        { key: 'owner', name: 'Owner', description: 'Full control of billing and membership.' },
        { key: 'manager', name: 'Manager', description: 'Manage licenses, members, and projects.' },
        { key: 'member', name: 'Member', description: 'Use assigned licenses.' },
        { key: 'viewer', name: 'Viewer', description: 'Read-only.' },
    ];

    private teams: TeamSummary[] = [
        {
            id: 'team-001',
            name: 'Demo Builders',
            description: 'Sample team for subscription previews.',
            createdUtc: '2023-09-01T12:00:00Z',
            createdByUserId: 'user-001',
            members: [
                {
                    userId: 'user-001',
                    email: 'owner@example.com',
                    displayName: 'Alex Owner',
                    joinedUtc: '2023-09-01T12:00:00Z',
                    isActive: true,
                    roleKeys: ['owner'],
                    assignedLicenseIds: [],
                },
                {
                    userId: 'user-002',
                    email: 'planner@example.com',
                    displayName: 'Parker Planner',
                    joinedUtc: '2023-09-02T10:00:00Z',
                    isActive: true,
                    roleKeys: ['manager'],
                    assignedLicenseIds: [],
                },
            ],
            subscriptions: [
                {
                    id: 'sub-team-001',
                    planCode: 'team-pro',
                    planName: 'Team Pro',
                    description: 'Team-wide Pro subscription for 5 seats.',
                    provider: 'stripe',
                    status: 'active',
                    seatsPurchased: 5,
                    seatsInUse: 2,
                    autoRenews: true,
                    renewsUtc: '2024-10-15T00:00:00Z',
                    licenseTypeKey: 'pro',
                    linkedTeamId: 'team-001',
                },
            ],
            licenses: [
                {
                    id: 'lic-001',
                    licenseTypeKey: 'pro',
                    licenseTypeName: 'Pro',
                    licenseTypeDescription: 'Full access with integrations.',
                    ownerTeamId: 'team-001',
                    issuedUtc: '2023-09-01T12:00:00Z',
                    isActive: true,
                    assignedUserId: 'user-001',
                    assignedUserDisplayName: 'Alex Owner',
                },
                {
                    id: 'lic-002',
                    licenseTypeKey: 'pro',
                    licenseTypeName: 'Pro',
                    licenseTypeDescription: 'Full access with integrations.',
                    ownerTeamId: 'team-001',
                    issuedUtc: '2023-09-01T12:00:00Z',
                    isActive: true,
                    assignedUserId: 'user-002',
                    assignedUserDisplayName: 'Parker Planner',
                },
                {
                    id: 'lic-003',
                    licenseTypeKey: 'pro',
                    licenseTypeName: 'Pro',
                    licenseTypeDescription: 'Full access with integrations.',
                    ownerTeamId: 'team-001',
                    issuedUtc: '2023-09-01T12:00:00Z',
                    isActive: true,
                },
            ],
            roleOptions: [],
            pendingInvites: [],
        },
        {
            id: 'team-002',
            name: 'Field Ops',
            description: 'Field operations and site planners.',
            createdUtc: '2024-01-10T09:00:00Z',
            createdByUserId: 'user-003',
            members: [
                {
                    userId: 'user-003',
                    email: 'owner2@example.com',
                    displayName: 'Taylor Owner',
                    joinedUtc: '2024-01-10T09:00:00Z',
                    isActive: true,
                    roleKeys: ['owner'],
                    assignedLicenseIds: [],
                },
                {
                    userId: 'user-004',
                    email: 'viewer2@example.com',
                    displayName: 'Vera Viewer',
                    joinedUtc: '2024-02-01T09:00:00Z',
                    isActive: true,
                    roleKeys: ['viewer'],
                    assignedLicenseIds: [],
                },
                {
                    userId: this.currentUserId,
                    email: 'you@example.com',
                    displayName: 'You (Manager)',
                    joinedUtc: '2024-02-15T09:00:00Z',
                    isActive: true,
                    roleKeys: ['manager'],
                    assignedLicenseIds: [],
                },
            ],
            subscriptions: [
                {
                    id: 'sub-team-002',
                    planCode: 'team-planner',
                    planName: 'Team Planner',
                    description: 'Planner tier for field ops with 3 seats.',
                    provider: 'stripe',
                    status: 'active',
                    seatsPurchased: 3,
                    seatsInUse: 1,
                    autoRenews: true,
                    renewsUtc: '2024-11-20T00:00:00Z',
                    licenseTypeKey: 'planner',
                    linkedTeamId: 'team-002',
                },
            ],
            licenses: [
                {
                    id: 'lic-101',
                    licenseTypeKey: 'planner',
                    licenseTypeName: 'Planner',
                    licenseTypeDescription: 'Planning tools.',
                    ownerTeamId: 'team-002',
                    issuedUtc: '2024-01-10T09:00:00Z',
                    isActive: true,
                    assignedUserId: 'user-004',
                    assignedUserDisplayName: 'Vera Viewer',
                },
                {
                    id: 'lic-102',
                    licenseTypeKey: 'planner',
                    licenseTypeName: 'Planner',
                    licenseTypeDescription: 'Planning tools.',
                    ownerTeamId: 'team-002',
                    issuedUtc: '2024-01-10T09:00:00Z',
                    isActive: true,
                    assignedUserId: this.currentUserId,
                    assignedUserDisplayName: 'You (Manager)',
                },
                {
                    id: 'lic-103',
                    licenseTypeKey: 'planner',
                    licenseTypeName: 'Planner',
                    licenseTypeDescription: 'Planning tools.',
                    ownerTeamId: 'team-002',
                    issuedUtc: '2024-01-10T09:00:00Z',
                    isActive: true,
                },
            ],
            roleOptions: [],
            pendingInvites: [],
        },
    ];

    constructor(private readonly $q: ng.IQService, private readonly $timeout: ng.ITimeoutService) {
        this.teams.forEach(team => team.roleOptions = this.roleOptions);
    }

    getLicenseTypes() {
        return this.respond(this.licenseTypes);
    }

    getPersonalSubscriptions() {
        return this.respond(this.subscriptions.filter(s => !s.linkedTeamId));
    }

    upsertPersonalSubscription(request: PersonalSubscriptionUpsertRequest) {
        let target: Nullable<SubscriptionSummary> = null;
        if (request.subscriptionId) {
            target = this.subscriptions.find(s => s.id === request.subscriptionId);
        }

        if (!target) {
            target = {
                id: this.makeId('sub-personal'),
                planCode: request.planCode,
                planName: request.planName || request.planCode,
                provider: 'manual',
                status: 'active',
                seatsPurchased: request.seatsPurchased,
                seatsInUse: 0,
                autoRenews: !!request.autoRenews,
            };
            this.subscriptions.push(target);
        }

        target.planCode = request.planCode;
        target.planName = request.planName || target.planName;
        target.seatsPurchased = request.seatsPurchased;
        target.licenseTypeKey = request.licenseTypeKey;
        target.autoRenews = request.autoRenews ?? target.autoRenews;

        if (!target) {
            return this.$q.reject('Subscription not found');
        }
        return this.respond(target);
    }

    cancelPersonalSubscription(subscriptionId: string) {
        const target = this.subscriptions.find(s => s.id === subscriptionId);
        if (target) {
            target.status = 'canceled';
            target.autoRenews = false;
        }
        return this.respond<void>(undefined);
    }

    getTeams() {
        return this.respond(this.teams);
    }

    createTeam(request: TeamUpsertRequest) {
        const newTeam: TeamSummary = {
            id: this.makeId('team'),
            name: request.name,
            description: request.description,
            createdUtc: new Date().toISOString(),
            createdByUserId: 'current-user',
            members: [],
            subscriptions: [],
            licenses: [],
            roleOptions: this.roleOptions,
            pendingInvites: [],
        };
        this.teams.push(newTeam);
        return this.respond(newTeam);
    }

    updateTeam(teamId: string, request: TeamUpsertRequest) {
        const team = this.findTeam(teamId);
        if (team) {
            team.name = request.name;
            team.description = request.description;
        }
        if (!team) {
            return this.$q.reject('Team not found');
        }
        return this.respond(team);
    }

    deleteTeam(teamId: string) {
        this.teams = this.teams.filter(t => t.id !== teamId);
        return this.respond<void>(undefined);
    }

    inviteTeamMember(teamId: string, request: TeamMemberInviteRequest) {
        const team = this.findTeam(teamId);
        if (team) {
            team.pendingInvites.push({
                email: request.email,
                invitedByUserId: 'current-user',
                invitedUtc: new Date().toISOString(),
                roleKeys: request.roleKeys,
            } as TeamInviteSummary);

            const newUserId = this.makeId('user');
            team.members.push({
                userId: newUserId,
                email: request.email,
                displayName: request.displayName || request.email,
                joinedUtc: new Date().toISOString(),
                isActive: true,
                roleKeys: request.roleKeys,
                assignedLicenseIds: [],
            });
        }
        if (!team) {
            return this.$q.reject('Team not found');
        }
        return this.respond(team);
    }

    removeTeamMember(teamId: string, userId: string) {
        const team = this.findTeam(teamId);
        if (team) {
            team.members = team.members.filter(m => m.userId !== userId);
            team.licenses.forEach(lic => {
                if (lic.assignedUserId === userId) {
                    lic.assignedUserId = undefined;
                    lic.assignedUserDisplayName = undefined;
                }
            });
        }
        if (!team) {
            return this.$q.reject('Team not found');
        }
        return this.respond(team);
    }

    updateTeamMemberRole(teamId: string, userId: string, request: TeamMemberRoleUpdateRequest) {
        const member = this.findMember(teamId, userId);
        if (member) {
            member.roleKeys = request.roleKeys;
        }
        const team = this.findTeam(teamId);
        if (!team) {
            return this.$q.reject('Team not found');
        }
        return this.respond(team);
    }

    getLicenses(teamId?: string) {
        if (!teamId) {
            const allLicenses = this.teams.flatMap(t => t.licenses);
            return this.respond(allLicenses);
        }

        const team = this.findTeam(teamId);
        if (!team) {
            return this.$q.reject('Team not found');
        }
        return this.respond(team.licenses);
    }

    assignLicense(request: LicenseAssignmentRequest) {
        const license = this.findLicense(request.licenseId);
        if (!license) {
            return this.$q.reject('License not found');
        }

        const member = request.teamId ? this.findMember(request.teamId, request.userId) : this.findMemberByUserId(request.userId);
        if (!member) {
            return this.$q.reject('User not found for license assignment');
        }
        license.assignedUserId = member.userId;
        license.assignedUserDisplayName = member.displayName;
        member.assignedLicenseIds = member.assignedLicenseIds || [];
        if (member.assignedLicenseIds.indexOf(license.id) === -1) {
            member.assignedLicenseIds.push(license.id);
        }

        return this.respond(license);
    }

    unassignLicense(licenseId: string) {
        const license = this.findLicense(licenseId);
        if (license) {
            if (license.assignedUserId) {
                const member = this.findMemberByUserId(license.assignedUserId);
                if (member && member.assignedLicenseIds) {
                    member.assignedLicenseIds = member.assignedLicenseIds.filter(id => id !== licenseId);
                }
            }
            license.assignedUserId = undefined;
            license.assignedUserDisplayName = undefined;
        }
        if (!license) {
            return this.$q.reject('License not found');
        }
        return this.respond(license);
    }

    linkSubscriptionToTeam(request: SubscriptionLinkRequest) {
        const team = this.findTeam(request.teamId);
        const subscription = this.subscriptions.find(s => s.id === request.subscriptionId);

        if (!team || !subscription) {
            return this.$q.reject('Team or subscription not found');
        }

        subscription.linkedTeamId = team.id;
        subscription.seatsPurchased = request.seatCount ?? subscription.seatsPurchased;

        if (team.subscriptions.every(s => s.id !== subscription.id)) {
            team.subscriptions.push({ ...subscription });
        }

        return this.respond(team);
    }

    updateSubscriptionSeats(request: SeatUpdateRequest) {
        const subscription = this.subscriptions.find(s => s.id === request.subscriptionId);
        if (!subscription) {
            return this.$q.reject('Subscription not found');
        }

        subscription.seatsPurchased = request.seatsPurchased;

        this.teams.forEach(team => {
            const teamSub = team.subscriptions.find(s => s.id === request.subscriptionId);
            if (teamSub) {
                teamSub.seatsPurchased = request.seatsPurchased;
            }
        });

        return this.respond(subscription);
    }

    getCurrentUserTeamRole(teamId: string) {
        const team = this.findTeam(teamId);
        const member = team?.members.find(m => m.userId === this.currentUserId);
        const roleKeys = member ? member.roleKeys : ['viewer'];
        const canManage = roleKeys.indexOf('owner') >= 0 || roleKeys.indexOf('manager') >= 0;
        return this.respond({ teamId, roleKeys, canManage });
    }

    private respond<T>(data: T) {
        const copy = angular.copy(data) as T;
        return this.$timeout(() => copy, 150);
    }

    private makeId(prefix: string) {
        return `${prefix}-${Math.random().toString(36).substring(2, 8)}`;
    }

    private findTeam(teamId: string) {
        return this.teams.find(t => t.id === teamId);
    }

    private findMember(teamId: string, userId: string) {
        const team = this.findTeam(teamId);
        return team?.members.find(m => m.userId === userId);
    }

    private findMemberByUserId(userId: string) {
        for (const team of this.teams) {
            const member = team.members.find(m => m.userId === userId);
            if (member) {
                return member;
            }
        }
        return null;
    }

    private findLicense(licenseId: string) {
        for (const team of this.teams) {
            const license = team.licenses.find(l => l.id === licenseId);
            if (license) {
                return license;
            }
        }
        return null;
    }
}

gpHome.service(SubscriptionManagementMockService.serviceName, SubscriptionManagementMockService);
